import { LitElement, html, css } from 'lit';
import { property, customElement, query } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';

import { authenticatedAPICall } from '../../client/api-client';
import {
  formatDateInput,
  formatDate,
  formatTime,
  formatTimeWithoutDate,
} from '../../util/util';
import '../../elements/pickup-order/pickup-order';
import '../../elements/add-comment/add-comment';
import { AddComment } from '../../elements/add-comment/add-comment';
import { linkIcon } from '../../elements/icons/icons';
import { Comment, ProductionUnit, StockCount } from '../../types/types';

type MachineSummary = {
  production_unit_name: string;
  product_summaries: ProductSummary[];
};

type ProductSummary = {
  product_code: string;
  product_name: string;
  product_pieces_per_pack: number;
  product_pieces_per_unit: number;
  product_weight: number;
  count: number;
  count_in_units: number;
};

@customElement('langhout-beton-stock-count-done')
export class LanghoutBetonStockCountDone extends LitElement {
  static styles = css`
    input {
      padding: 7px;
      border-radius: 5px;
      font-size: 16px;
    }

    .card {
      border: 1px solid var(--text-color-main);
      padding: 12px;
      margin-bottom: 20px;
      border-radius: 10px;
      position: relative;
    }

    .row {
      width: 100%;
      display: inline-flex;
      flex-direction: row;
      justify-content: left;
    }

    .wrap-row {
      flex-wrap: wrap;
      flex-flow: row wrap;
    }

    .fill {
      flex-grow: 1;
    }

    .prop-wrapper {
      display: flex;
      flex-direction: row;
      flex-wrap: nowrap;
      flex-flow: nowrap;
      width: 345px;
      max-width: 100%;
    }

    .label {
      color: var(--primary-color);
      min-width: 130px;
      text-align: left;
    }

    .normal-text {
      flex: 1 1 auto;
      text-align: left;
      min-width: 0;
      max-width: 200px;
      padding-right: 15px;
      overflow-wrap: break-word;
    }

    .icon-space {
      flex: 0 0 24px;
    }

    .comment-wrapper {
      margin-top: 10px;
      padding: 10px;
      border: 1px solid var(--text-color-main);
      border-radius: 10px;
    }

    .comment-user-name,
    .comment-time {
      color: var(--primary-color);
      font-weight: bold;
    }

    add-comment {
      z-index: 9999;
      display: none;
      position: absolute;
      top: 0;
      left: 0;
    }

    :host([displayAddComment]) add-comment {
      display: flex;
    }

    langhout-button {
      margin-top: 20px;
    }

    .centered {
      justify-content: center;
      align-items: center;
    }

    .summary-wrapper {
      margin-bottom: 30px;
    }

    .summary-row {
      display: flex;
      flex-direction: row;
    }

    .summary-value {
      width: 220px;
      max-width: calc(100vw / 3);
    }

    .summary-value-header {
      width: 220px;
      color: var(--primary-color);
      max-width: calc(100vw / 3);
      font-weight: bold;
    }

    select {
      padding: 5px;
      height: 37.19px;
      border-radius: 5px;
      border: 2px solid black;
      font-size: 16px;
      margin-bottom: 15px;
    }
  `;

  private limit = 100;
  private offset = 0;

  @property({ type: Array })
  private stockCounts: StockCount[] = [];

  @query('#showDate')
  public showDate?: HTMLInputElement;

  private currentDate = new Date();

  @property({ type: Boolean, reflect: true })
  private displayAddComment = false;

  private machineSummaries: MachineSummary[] = [];

  private totalWeight = 0;

  @property({ type: Array })
  private productionUnits: ProductionUnit[] = [];

  @query('#productionUnitDropdown')
  private productionUnitSelect?: HTMLSelectElement;

  private selectedProductionUnitIndex = 0;

  constructor() {
    super();
    this.listProductionUnits();
    this.listStockCount();
  }

  render() {
    if (this.stockCounts.length === 0) {
      return html`
        <input
          type="date"
          id="showDate"
          name="showDate"
          value="${formatDateInput(this.currentDate.toISOString())}"
          @change="${() => {
            this.listStockCount();
          }}"
        />
        <h2>
          Er zijn geen gelost karren op
          ${formatDate(this.currentDate.toISOString())}
        </h2>
        <select
          id="productionUnitDropdown"
          name="machine"
          @change="${() => {
            this.listStockCount();
          }}"
        >
          ${this.productionUnits.map((productionUnit, i) => {
            return html`
              <option
                .selected=${i == this.selectedProductionUnitIndex}
                value="${productionUnit.id}"
              >
                ${productionUnit.name}
              </option>
            `;
          })}
        </select>
      `;
    }
    return html`
      <add-comment
        id="add-comment"
        @close="${() => {
          this.displayAddComment = false;
          this.listStockCount();
        }}"
      ></add-comment>
      <input
        type="date"
        id="showDate"
        name="showDate"
        value="${formatDateInput(this.currentDate.toISOString())}"
        @change="${() => {
          this.listStockCount();
        }}"
      />
      <h2>Gelost op ${formatDate(this.currentDate.toISOString())}</h2>
      ${this.renderSummary()}
      <select
        id="productionUnitDropdown"
        name="machine"
        @change="${() => {
          this.listStockCount();
        }}"
      >
        ${this.productionUnits.map((productionUnit, i) => {
          return html`
            <option
              .selected=${i == this.selectedProductionUnitIndex}
              value="${productionUnit.id}"
            >
              ${productionUnit.name}
            </option>
          `;
        })}
      </select>
      ${repeat(
        this.stockCounts,
        stockCount => stockCount.id,
        stockCount => {
          return html`
            <div class="card">
              <div class="row wrap-row">
                <div class="prop-wrapper">
                  <div class="label">Machine:</div>
                  <div class="normal-text">
                    ${stockCount.production_unit_name}
                  </div>
                </div>
                <div class="prop-wrapper">
                  <div class="label">Los datum:</div>
                  <div class="normal-text">${formatDate(stockCount.time)}</div>
                </div>
              </div>
              <div class="row wrap-row">
                <div class="prop-wrapper">
                  <div class="label">Product code:</div>
                  <div class="normal-text">${stockCount.product_name}</div>
                </div>
                <div class="prop-wrapper">
                  <div class="label">Los tijd:</div>
                  <div class="normal-text">
                    ${formatTimeWithoutDate(stockCount.time)}
                  </div>
                    <div class="icon-space">
                      ${linkIcon(
                        stockCount.automatic_stock_count ? '#008000' : undefined,
                      )}
                    </div>
                </div>
              </div>
              <div class="row wrap-row">
                <div class="prop-wrapper">
                  <div class="label">Stuks:</div>
                  <div class="normal-text">${stockCount.count}</div>
                </div>
                <div class="prop-wrapper">
                  <div class="label">Gelost door:</div>
                  <div class="normal-text">${stockCount.created_user}</div>
                  <div class="icon-space"></div>
                </div>
              </div>
              ${this.renderComments(stockCount)}
              <div class="row wrap-row">
                ${this.renderAddCommentButton(stockCount)}
              </div>
            </div>
          `;
        },
      )}
    `;
  }

  renderSummary = () => {
    return html`
      <h3>
        Tonnage: ${Math.round(this.totalWeight).toLocaleString('nl-NL')} kg
      </h3>
      ${this.machineSummaries.map((value: MachineSummary) => {
        return html`
          <h2>${value.production_unit_name}</h2>

          <div class="row centered">
            <div class="summary-wrapper column">
              <div class="summary-row">
                <div class="summary-value-header">Product</div>
                <div class="summary-value-header">Aantal</div>
                <div class="summary-value-header">Gewicht</div>
              </div>
              ${value.product_summaries.map((value: ProductSummary) => {
                return html`
                  <div class="summary-row">
                    <div class="summary-value">${value.product_name}</div>
                    <div class="summary-value">${value.count} stuks</div>
                    <div class="summary-value">
                      ${Math.round(
                        value.count * value.product_weight,
                      ).toLocaleString('nl-NL')}
                      kg
                    </div>
                  </div>
                `;
              })}
            </div>
          </div>
        `;
      })}
    `;
  };

  renderAddCommentButton = (stockCount: StockCount) => {
    return html`
      <langhout-button
        reverse
        text="Opmerking toevoegen"
        @click="${() => {
          this.displayAddComment = true;
          setTimeout(() => {
            if (this.shadowRoot) {
              const addCommentElement = <AddComment>(
                this.shadowRoot.querySelector(`#add-comment`)
              );
              if (!addCommentElement) {
                this.displayAddComment = false;
                return;
              }
              addCommentElement.url = `/stock/count/${stockCount.id}/comment`;
              addCommentElement.focus();
            }
          }, 10);
        }}"
      ></langhout-button>
    `;
  };

  renderComments = (stockCount: StockCount) => {
    if (!stockCount.comments || stockCount.comments.length === 0) {
      return html``;
    }
    return html`
      ${stockCount.comments.map((comment: Comment) => {
        return html`
          <div class="comment-wrapper">
            <div class="row">
              <div class="comment-user-name">${comment.user_name}</div>
              <div class="fill"></div>
              <div class="comment-time">
                ${formatTime(comment.timestamp, false)}
              </div>
            </div>
            <div class="row">${comment.message}</div>
          </div>
        `;
      })}
    `;
  };

  listProductionUnits = async () => {
    const path = `/production-unit`;

    const result = await authenticatedAPICall(path, 'GET', new Headers());
    if (result.statusCode !== undefined && result.statusCode >= 200 && result.statusCode < 300 && Array.isArray(result.body)) {
      this.productionUnits = [];

      this.productionUnits.push(<ProductionUnit>{
        id: '',
        name: 'Alles',
      });
      (result.body as ProductionUnit[]).forEach((e: ProductionUnit) => {
        this.productionUnits.push(e);
      });
    }
  };

  listStockCount = async () => {
    let showDoneFromDate: string = formatDate(new Date().toISOString());
    const showDate = this.showDate ? this.showDate.value : '';
    if (showDate != '') {
      this.currentDate = new Date(showDate);
      showDoneFromDate = formatDate(showDate);
    }

    let productionUnitID = '';
    if (this.productionUnitSelect) {
      productionUnitID = this.productionUnitSelect.value;
      this.selectedProductionUnitIndex =
        this.productionUnitSelect.selectedIndex;
    }

    const path = `/stock/count?order_by=created_at&order_direction=desc&limit=${this.limit}&from=${showDoneFromDate}&until=${showDoneFromDate}&offset=${this.offset}&production_unit_ids=${productionUnitID}`;

    const result = await authenticatedAPICall(path, 'GET', new Headers());
    if (result.statusCode === undefined || result.statusCode < 200 || result.statusCode >= 300 || !Array.isArray(result.body)) {
      return;
    }
    this.stockCounts = result.body as StockCount[];

    const stockCountPerMachineMap: Map<
      string,
      Map<string, ProductSummary>
    > = new Map();

    let totalWeight = 0;
    this.stockCounts.forEach(v => {
      totalWeight += v.count * v.product_weight;
      const countsPerMachine = stockCountPerMachineMap.get(
        v.production_unit_name,
      );
      if (!countsPerMachine) {
        const perProductMap: Map<string, ProductSummary> = new Map();
        perProductMap.set(v.product_code, this.stockCountToProductSummary(v));
        stockCountPerMachineMap.set(v.production_unit_name, perProductMap);
      } else {
        const ProductSummary = countsPerMachine.get(v.product_code);
        if (!ProductSummary) {
          countsPerMachine.set(
            v.product_code,
            this.stockCountToProductSummary(v),
          );
        } else {
          ProductSummary.count += v.count;
          ProductSummary.count_in_units += v.count_in_units;
          countsPerMachine.set(v.product_code, ProductSummary);
        }
        stockCountPerMachineMap.set(v.production_unit_name, countsPerMachine);
      }
    });
    this.totalWeight = totalWeight;

    const res: MachineSummary[] = [];
    stockCountPerMachineMap.forEach(
      (value: Map<string, ProductSummary>, key: string) => {
        const summaries: ProductSummary[] = [];
        value.forEach((value: ProductSummary) => {
          summaries.push(value);
        });

        res.push({
          production_unit_name: key,
          product_summaries: summaries,
        });
      },
    );
    this.machineSummaries = res;
  };

  stockCountToProductSummary = (v: StockCount): ProductSummary => {
    return <ProductSummary>{
      product_code: v.product_code,
      product_name: v.product_name,
      product_pieces_per_pack: v.product_pieces_per_pack,
      product_pieces_per_unit: v.product_pieces_per_unit,
      product_weight: v.product_weight,
      count: v.count,
      count_in_units: v.count_in_units,
    };
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'langhout-beton-stock-count-done': LanghoutBetonStockCountDone;
  }
}
